const PAYMENT_REQUEST_ALLOWED_STATUSES = new Set(['paid', 'unpaid', 'overdue', 'partial', 'received']);
const INVOICE_ALLOWED_STATUSES = new Set(['paid', 'unpaid', 'overdue', 'partial', 'received', 'forwarded', 'current']);

const STATUS_CANONICAL_MAP = {
  paid: 'Paid',
  unpaid: 'Unpaid',
  overdue: 'Overdue',
  partial: 'Partial',
  received: 'Received',
  forwarded: 'Forwarded',
  current: 'Current',
};

const normalizeStatusKey = (raw) =>
  String(raw ?? '')
    .toLowerCase()
    .replace(/[\s_]+/g, '');

const toDisplayStatus = (raw) => {
  if (!raw) return '';
  return String(raw)
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Normalizes a raw status string to an allowed canonical value for the given section.
 *
 * @param {string} raw - Raw status from API
 * @param {Set<string>} allowedSet - Allowed normalized status keys
 * @returns {string} Canonical status for section display
 */
export const normalizeBillingItemStatus = (raw, allowedSet) => {
  const key = normalizeStatusKey(raw);
  if (allowedSet.has(key) && STATUS_CANONICAL_MAP[key]) {
    return STATUS_CANONICAL_MAP[key];
  }
  return toDisplayStatus(raw);
};

const resolveItemStatus = (item) => item?.status ?? item?.payment_request_status ?? item?.invoice_status ?? '';

const normalizeAmountFields = (item) => ({
  ...item,
  amount_due: item?.amount_due ?? item?.due_amount,
  amount_paid: item?.amount_paid ?? item?.paid_amount,
});

/**
 * Normalizes a subscription payment request item for shared section consumption.
 *
 * @param {object} item - Raw payment request row
 * @returns {object} Normalized payment request row
 */
export const normalizeSubscriptionPaymentRequestItem = (item = {}) => {
  const withAmounts = normalizeAmountFields(item);
  const status = normalizeBillingItemStatus(resolveItemStatus(item), PAYMENT_REQUEST_ALLOWED_STATUSES);

  return {
    ...withAmounts,
    status,
  };
};

/**
 * Normalizes a subscription invoice item for shared section consumption.
 *
 * @param {object} item - Raw invoice row
 * @returns {object} Normalized invoice row
 */
export const normalizeSubscriptionInvoiceItem = (item = {}) => {
  const withAmounts = normalizeAmountFields(item);
  const status = normalizeBillingItemStatus(resolveItemStatus(item), INVOICE_ALLOWED_STATUSES);

  return {
    ...withAmounts,
    status,
  };
};
